import { hasPermission, PERM } from '../accessPermissions';

/** 有审批记录菜单才出现「审批记录」。销售、财务、助理等模板没有这项。 */
export const seesApproverApprovalList = (userInfo) => hasPermission(userInfo, PERM.MENU_APPROVAL_RECORDS);

/** 有申请记录菜单才出现「我的申请」。 */
export const seesApplicantApprovalList = (userInfo) => hasPermission(userInfo, PERM.MENU_APPROVAL_APPLICATIONS);

/** 首页与列表共用的入口名。权限仍分申请、审批两套，只是不再各占一个入口。 */
export const APPROVAL_LIST_HUB_LABEL = '审批信息';

/** 两种菜单都有时，列表里才能在「我的申请 / 审批记录」之间切换。 */
export const canSwitchApprovalListScope = (userInfo) => seesApplicantApprovalList(userInfo) && seesApproverApprovalList(userInfo);

/**
 * 看哪一侧只看菜单权限，不看角色。
 * 只有审批记录 → 审批记录；只有申请记录 → 我的申请。
 * 两种都有时，地址上的 scope 优先；没带 scope 则先看审批记录。
 * @param {object|null|undefined} userInfo
 * @param {string|null|undefined} scope
 */
export const resolveApprovalListSeesAll = (userInfo, scope) => {
	const records = seesApproverApprovalList(userInfo);
	const applications = seesApplicantApprovalList(userInfo);
	if (!records) return false;
	if (!applications) return true;
	return scope !== 'mine';
};

export const getApprovalListHubLabel = () => APPROVAL_LIST_HUB_LABEL;

export const getApprovalListHubPath = (tab, scope) => {
	const params = new URLSearchParams();
	if (tab === 'order' || tab === 'customer' || tab === 'quota' || tab === 'finalCost') {
		params.set('tab', tab);
	}
	if (scope === 'mine' || scope === 'all') params.set('scope', scope);
	const qs = params.toString();
	return qs ? `/approval/list?${qs}` : '/approval/list';
};

/** 从列表进详情时带上当前范围，返回时仍停在同一侧。 */
export const withApprovalListScope = (path, scope) => {
	if (!path || (scope !== 'mine' && scope !== 'all')) return path;
	const hashAt = path.indexOf('#');
	const hash = hashAt >= 0 ? path.slice(hashAt) : '';
	const before = hashAt >= 0 ? path.slice(0, hashAt) : path;
	if (/(?:^|[?&])scope=/.test(before)) return path;
	const joiner = before.includes('?') ? '&' : '?';
	return `${before}${joiner}scope=${scope}${hash}`;
};

/** 首页「被驳回」待办（申请侧） */
export const getRejectedPendingDashboardPath = () => '/dashboard?pendingTab=Rejected';

/** @deprecated 使用 getRejectedPendingDashboardPath；保留别名避免漏改 */
export const getOrderRejectedListPath = () => getRejectedPendingDashboardPath();


export const getOrderApprovalDetailPath = (item) => {
	const { _id, detail, status } = item || {};
	if (detail?.adjustmentType === 'finalCost') {
		return detail?.orderOID && _id ? `/approval/final-cost/${detail.orderOID}/detail/${_id}` : null;
	}
	if (status === 'withdrawn') {
		return _id ? `/approval/order/detail/${_id}` : null;
	}
	if (detail?.orderOID && _id) {
		return `/approval/order/${detail.orderOID}/detail/${_id}`;
	}
	return _id ? `/approval/order/detail/${_id}` : null;
};

const userIdOf = (user) => {
	if (user == null || user === '') return '';
	if (typeof user === 'object') return user._id ? String(user._id) : '';
	return String(user);
};

/**
 * 实际创建人高亮。
 * 客户、额度：有销售助理时标助理，否则标申请人。
 * 订单：订单 createdUser 是提交人，销售助理只是订单上的助理。
 * 最终成本：提交人是申请人。
 */
export const getActualCreatorHighlight = ({ entryKind, applicant, assistant, orderCreatedUser } = {}) => {
	const applicantId = userIdOf(applicant);
	const assistantId = userIdOf(assistant);
	if (entryKind === 'order') {
		const creatorId = userIdOf(orderCreatedUser);
		if (!creatorId) return { applicant: false, assistant: false };
		return {
			applicant: Boolean(applicantId) && applicantId === creatorId,
			assistant: Boolean(assistantId) && assistantId === creatorId,
		};
	}
	if (entryKind === 'finalCost') {
		return {
			applicant: Boolean(applicantId),
			assistant: Boolean(assistantId) && assistantId === applicantId,
		};
	}
	return {
		applicant: !assistantId,
		assistant: Boolean(assistantId),
	};
};

/** 待办「申请人」列显示实际提交人。客户、额度有销售助理时提交人是助理。 */
export const getPendingListApplicant = (row) => {
	const kind = row?.entryKind === 'customer' || row?.entryKind === 'quota' ? row.entryKind : 'order';
	if (kind === 'order') {
		const created = row?.createdUser;
		const info = row?.createdUserInfo;
		const id = userIdOf(created) || userIdOf(info);
		const name = (typeof created === 'object' && created?.name) || info?.name || '';
		if (!id && !name) return null;
		return { _id: id, name };
	}
	const applicant = row?.createdUser || null;
	const assistant = row?.assistantUser || null;
	const highlight = getActualCreatorHighlight({ entryKind: kind, applicant, assistant });
	if (highlight.assistant) return assistant || applicant;
	return applicant;
};

export const isPendingListApplicantHighlighted = (row) => {
	const shown = getPendingListApplicant(row);
	if (!shown?.name && !userIdOf(shown)) return false;
	const kind = row?.entryKind === 'customer' || row?.entryKind === 'quota' ? row.entryKind : 'order';
	if (kind !== 'order') return true;
	return getActualCreatorHighlight({
		entryKind: 'order',
		applicant: shown,
		assistant: row?.assistantUserInfo || row?.assistantUser,
		orderCreatedUser: row?.createdUser || row?.createdUserInfo,
	}).applicant;
};

/** 首页待办：Approval 客户单 → 与订单待办行可混排的形状 */
export const mapCustomerApprovalToPendingRow = (approval) => {
	if (!approval) return null;
	const detail = approval.detail || {};
	const applyAt = approval.createdAt || null;
	return {
		entryKind: 'customer',
		_id: approval._id,
		approval,
		customerInfo: { customerName: detail.newCustomerConcatName || '' },
		createdUser: approval.applicant,
		assistantUser: approval.assistant,
		createdAt: approval.createdAt,
		updatedAt: approval.updatedAt,
		approvalStatus: approval.status,
		orderId: '',
		title: approval.title || '',
		// 与订单待办一致：入栏/申请时刻，供 NEW 与「申请时间/提交时间」列共用
		uiHints: {
			queueEnteredAt: applyAt,
			activityAt: applyAt,
		},
	};
};

/** 首页待办：最终成本修正申请 → 与订单待办行可混排的形状 */
export const mapFinalCostApprovalToPendingRow = (approval) => {
	if (!approval || approval.detail?.adjustmentType !== 'finalCost') return null;
	const detail = approval.detail || {};
	const applyAt = approval.createdAt || null;
	return {
		entryKind: 'finalCost',
		_id: approval._id,
		approval,
		customerInfo: { customerName: detail.customerName || '' },
		saleUser: detail.saleUserName ? { _id: detail.saleUserId || '', name: detail.saleUserName } : null,
		saleUserInfo: detail.saleUserName ? { _id: detail.saleUserId || '', name: detail.saleUserName } : null,
		createdUser: approval.applicant,
		assistantUser: approval.assistant,
		createdAt: approval.createdAt,
		updatedAt: approval.updatedAt,
		approvalStatus: approval.status,
		orderId: detail.orderId || '',
		title: approval.title || '',
		uiHints: {
			queueEnteredAt: applyAt,
			activityAt: applyAt,
		},
	};
};

/** 被驳回列表：驳回原因用审批意见，入栏时间用最近更新 */
export const mapRejectedFinalCostApprovalToPendingRow = (approval) => {
	const mapped = mapFinalCostApprovalToPendingRow(approval);
	if (!mapped) return null;
	const at = approval.updatedAt || approval.createdAt || null;
	return {
		...mapped,
		rejectReason: String(approval.approvalSuggestions || '').trim(),
		uiHints: {
			queueEnteredAt: at,
			activityAt: at,
		},
	};
};

/** 首页待办：额度申请 → 与订单、客户待办行可混排的形状 */
export const mapQuotaApprovalToPendingRow = (approval) => {
	if (!approval) return null;
	const detail = approval.detail || {};
	const applyAt = approval.createdAt || null;
	const plainTitle = String(approval.title || '').replace(/<[^>]+>/g, '');
	return {
		entryKind: 'quota',
		_id: approval._id,
		approval,
		customerInfo: { customerName: detail.customerName || '' },
		createdUser: approval.applicant,
		assistantUser: approval.assistant,
		createdAt: approval.createdAt,
		updatedAt: approval.updatedAt,
		approvalStatus: approval.status,
		orderId: '',
		title: plainTitle,
		uiHints: {
			queueEnteredAt: applyAt,
			activityAt: applyAt,
		},
	};
};

/**
 * 审批信息列表行：对齐待办的客户、销售员、销售助理、申请人。
 * 订单申请人用订单建单人；成本修正、客户、额度用审批单申请人。
 */
export const mapApprovalToListRow = (approval, entryKind) => {
	if (!approval) return null;
	const detail = approval.detail || {};
	const kind =
		entryKind ||
		(detail.adjustmentType === 'finalCost' ? 'finalCost' : approval.type === 'customer' ? 'customer' : approval.type === 'quota' ? 'quota' : 'order');
	const applyAt = approval.createdAt || null;
	const customerName = kind === 'customer' ? detail.newCustomerConcatName || '' : detail.customerName || '';
	const saleName = detail.saleUserName || '';
	const saleId = detail.saleUserId || '';
	const orderCreator = detail.orderCreatedUserName ? { _id: detail.orderCreatedUserId || '', name: detail.orderCreatedUserName } : null;
	const orderAssistant = detail.orderAssistantName ? { _id: detail.orderAssistantId || '', name: detail.orderAssistantName } : null;
	return {
		entryKind: kind,
		_id: approval._id,
		approval,
		customerInfo: { customerName },
		saleUser: saleName ? { _id: saleId, name: saleName } : null,
		saleUserInfo: saleName ? { _id: saleId, name: saleName } : null,
		createdUser: kind === 'order' ? orderCreator || approval.applicant : approval.applicant,
		assistantUser: kind === 'order' ? orderAssistant || approval.assistant : approval.assistant,
		createdAt: approval.createdAt,
		updatedAt: approval.updatedAt,
		approvalStatus: approval.status,
		orderId: detail.orderId || '',
		title: approval.title || '',
		rejectReason: String(approval.approvalSuggestions || '').trim(),
		uiHints: {
			queueEnteredAt: applyAt,
			activityAt: applyAt,
		},
	};
};
